import { useState, useEffect } from 'react';
import { XMarkIcon, CloudArrowUpIcon, DocumentIcon } from '@heroicons/react/24/outline';
import { httpsCallable } from 'firebase/functions';
import { functions, db } from '../../config/firebase';
import { collection, query, where, getDocs, doc, updateDoc } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';

const FILE_CATEGORIES = [
  { value: 'AdvPyD', label: 'Pay Advance' },
  { value: 'CusAgr', label: 'Contract Agreement' },
  { value: 'CusIde', label: 'Govt ID' },
  { value: 'ConPht', label: 'Coach Photo' },
  { value: 'ComRCf', label: 'Commision Received' }
];

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export default function AgentFileUploadModal({ isOpen, onClose, onUploadSuccess }) {
  const { currentAgent } = useAuth();
  const [selectedFile, setSelectedFile] = useState(null);
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('AdvPyD');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [relatedParent, setRelatedParent] = useState('');
  
  // Ninox reference states
  const [ninoxReferences, setNinoxReferences] = useState([]);
  const [loadingNinoxRefs, setLoadingNinoxRefs] = useState(false);
  const [selectedNinoxRef, setSelectedNinoxRef] = useState('');
  const [selectedNinoxRefDocId, setSelectedNinoxRefDocId] = useState(''); // Store Firestore doc ID

  // Fetch Ninox references when category changes (except for CusIde and ConPht)
  useEffect(() => {
    const eligibleCategories = ['AdvPyD', 'CusAgr', 'ComRCf'];
    if (eligibleCategories.includes(category) && currentAgent?.code && isOpen) {
      fetchNinoxReferences();
    } else {
      setNinoxReferences([]);
      setSelectedNinoxRef('');
    }
  }, [category, currentAgent?.code, isOpen]);

  const fetchNinoxReferences = async () => {
    if (!currentAgent?.code) return;
    
    setLoadingNinoxRefs(true);
    try {
      const ninoxRefsRef = collection(db, 'x_ninox_reference');
      const q = query(
        ninoxRefsRef,
        where('agentCode', '==', currentAgent.code),
        where('category', '==', category),
        where('attached', '!=', true)
      );
      
      const querySnapshot = await getDocs(q);
      
      const references = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Always add "NO Reference Available" option at the end
      const referencesWithNotAvailable = [
        ...references,
        {
          id: '00000',
          reference: '00000',
          description: 'NO Reference Available'
        }
      ];
      
      setNinoxReferences(referencesWithNotAvailable);
    } catch (err) {
      console.error('Error fetching Ninox references:', err);
      setError('Failed to load reference records');
    } finally {
      setLoadingNinoxRefs(false);
    }
  };

  if (!isOpen) return null;

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setError(`File size exceeds 10MB limit. Selected file is ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
      return;
    }

    setSelectedFile(file);
    setError(null);
  };

  const handleUpload = async () => {
    if (!selectedFile || !currentAgent?.id) {
      setError('Please select a file to upload');
      return;
    }

    // Validate related parent for eligible categories (except CusIde and ConPht)
    const eligibleCategories = ['AdvPyD', 'CusAgr', 'ComRCf'];
    if (eligibleCategories.includes(category) && !relatedParent.trim()) {
      setError('Please select a reference record');
      return;
    }

    setUploading(true);
    setError(null);
    setUploadProgress(10);

    try {
      // Convert file to base64
      const reader = new FileReader();
      
      reader.onprogress = (e) => {
        if (e.lengthComputable) {
          const progress = Math.round((e.loaded / e.total) * 30) + 10; // 10-40%
          setUploadProgress(progress);
        }
      };

      reader.onload = async (e) => {
        try {
          const base64Data = e.target.result.split(',')[1];
          setUploadProgress(50);

          // Call Cloud Function
          const uploadFile = httpsCallable(functions, 'uploadAgentFile');

          const result = await uploadFile({
            agentId: currentAgent.id,
            fileName: selectedFile.name,
            fileData: base64Data,
            mimeType: selectedFile.type || 'application/octet-stream',
            description: description.trim(),
            category: category,
            relatedParent: relatedParent.trim()
          });

          setUploadProgress(100);
          setSuccess(true);
          
          // Mark Ninox reference as attached if applicable
          const eligibleCategories = ['AdvPyD', 'CusAgr', 'ComRCf'];
          if (eligibleCategories.includes(category) && relatedParent && relatedParent !== '00000' && selectedNinoxRefDocId) {
            try {
              const ninoxRefDocRef = doc(db, 'x_ninox_reference', selectedNinoxRefDocId);
              await updateDoc(ninoxRefDocRef, {
                attached: true,
                attachedAt: new Date().toISOString(),
                attachedBy: currentAgent.id
              });
              console.log('✅ Ninox reference marked as attached');
            } catch (markError) {
              console.error('Error marking Ninox reference as attached:', markError);
              // Don't fail the upload if marking fails
            }
          }
          
          // Call success callback with result
          if (onUploadSuccess) {
            onUploadSuccess(result.data);
          }

          // Auto-close after 5 seconds to show success message
          setTimeout(() => {
            handleForceClose();
          }, 5000);

        } catch (uploadError) {
          console.error('Upload error:', uploadError);
          setError(uploadError.message || 'Failed to upload file. Please try again.');
          setUploadProgress(0);
        } finally {
          setUploading(false);
        }
      };

      reader.onerror = () => {
        setError('Failed to read file. Please try again.');
        setUploading(false);
        setUploadProgress(0);
      };

      reader.readAsDataURL(selectedFile);

    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'Failed to upload file. Please try again.');
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleClose = () => {
    if (!uploading && !success) {
      setSelectedFile(null);
      setDescription('');
      setCategory('AdvPyD');
      setRelatedParent('');
      setSelectedNinoxRef('');
      setSelectedNinoxRefDocId('');
      setNinoxReferences([]);
      setError(null);
      setSuccess(false);
      setUploadProgress(0);
      onClose();
    }
  };

  const handleForceClose = () => {
    setSelectedFile(null);
    setDescription('');
    setCategory('AdvPyD');
    setRelatedParent('');
    setSelectedNinoxRef('');
    setSelectedNinoxRefDocId('');
    setNinoxReferences([]);
    setError(null);
    setSuccess(false);
    setUploadProgress(0);
    onClose();
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Handle drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (uploading) return;
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      handleFileSelect({ target: { files: [file] } });
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Upload File</h2>
            <button
              onClick={success ? handleForceClose : handleClose}
              disabled={uploading}
              className="text-gray-400 hover:text-gray-500 disabled:opacity-50"
              title={success ? "Close" : "Cancel"}
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1">
          {/* Success Message */}
          {success && (
            <div className="rounded-md bg-green-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-green-800">
                    File uploaded successfully!
                  </h3>
                  <div className="mt-2 text-sm text-green-700">
                    <p>Your file has been uploaded and is being synced to Google Drive in the background.</p>
                    <p className="mt-1 font-medium">You can close this window now.</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* File Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select File <span className="text-red-500">*</span>
            </label>
            <div 
              className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-gray-400 transition-colors"
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <div className="space-y-1 text-center">
                <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                <div className="flex text-sm text-gray-600">
                  <label
                    htmlFor="file-upload"
                    className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                  >
                    <span>Upload a file</span>
                    <input
                      id="file-upload"
                      name="file-upload"
                      type="file"
                      className="sr-only"
                      onChange={handleFileSelect}
                      disabled={uploading}
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.zip"
                    />
                  </label>
                  <p className="pl-1">or drag and drop</p>
                </div>
                <p className="text-xs text-gray-500">
                  PDF, DOC, XLS, images up to 10MB
                </p>
              </div>
            </div>
          </div>

          {/* Selected File Info */}
          {selectedFile && (
            <div className="flex items-center p-3 bg-gray-50 rounded-md">
              <DocumentIcon className="h-8 w-8 text-gray-400 mr-3" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {selectedFile.name}
                </p>
                <p className="text-xs text-gray-500">
                  {formatFileSize(selectedFile.size)}
                </p>
              </div>
              {!uploading && (
                <button
                  onClick={() => setSelectedFile(null)}
                  className="ml-2 text-gray-400 hover:text-gray-500"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              )}
            </div>
          )}

          {/* Category */}
          <div>
            <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-2">
              Category <span className="text-red-500">*</span>
            </label>
            <select
              id="category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              disabled={uploading}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              {FILE_CATEGORIES.map((cat) => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>
          </div>

          {/* Reference Record - For eligible categories (AdvPyD, CusAgr, ComRCf) */}
          {['AdvPyD', 'CusAgr', 'ComRCf'].includes(category) && (
            <div>
              <label htmlFor="relatedParent" className="block text-sm font-medium text-gray-700 mb-2">
                Reference Record <span className="text-red-500">*</span>
              </label>
              {loadingNinoxRefs ? (
                <div className="flex items-center justify-center py-3 text-sm text-gray-500">
                  <svg className="animate-spin h-5 w-5 mr-2 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Loading reference records...
                </div>
              ) : ninoxReferences.length === 0 ? (
                <div className="rounded-md bg-yellow-50 p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-yellow-800">
                        No available reference records found for this category.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <select
                    id="relatedParent"
                    value={relatedParent}
                    onChange={(e) => {
                      const selectedValue = e.target.value;
                      setRelatedParent(selectedValue);
                      setSelectedNinoxRef(selectedValue);
                      
                      // Store the Firestore document ID if not the "NO Reference Available" option
                      if (selectedValue !== '00000') {
                        const selectedRef = ninoxReferences.find(ref => ref.reference === selectedValue);
                        setSelectedNinoxRefDocId(selectedRef?.id || '');
                      } else {
                        setSelectedNinoxRefDocId('');
                      }
                    }}
                    disabled={uploading || loadingNinoxRefs}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="">Select a reference record...</option>
                    {ninoxReferences.map((ref) => (
                      <option key={ref.id} value={ref.reference}>
                        {ref.reference} - {ref.description}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    Select the reference record this file relates to
                  </p>
                </>
              )}
            </div>
          )}

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
              Description (Optional)
            </label>
            <textarea
              id="description"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={uploading}
              placeholder="Add a brief description of this file..."
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
          </div>

          {/* Upload Progress */}
          {uploading && (
            <div className="mt-4">
              <div className="flex justify-between text-sm text-gray-600 mb-1">
                <span>Uploading...</span>
                <span>{uploadProgress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-in-out" 
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
            </div>
          )}
        </div>

        {/* Fixed Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg flex justify-end space-x-3 flex-shrink-0">
          <button
            type="button"
            onClick={success ? handleForceClose : handleClose}
            disabled={uploading}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {success ? 'Close' : 'Cancel'}
          </button>
          {!success && (
            <button
              type="button"
              onClick={handleUpload}
              disabled={uploading || !selectedFile}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploading ? 'Uploading...' : 'Upload File'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
